import { AddCircle, Clear, Edit, Search, Upload } from "@mui/icons-material";
import { Box, Button, Card, CardContent, Grid, Tooltip } from "@mui/material";
import { GridActionsCellItem, GridRowId, GridRowModel, GridSortDirection, GridSortModel } from "@mui/x-data-grid";
import React, { useEffect, useMemo, useState } from "react";

import AutoComplete from "@/components/hookForms/autoComplete";
import { Container } from "@/theme/globalStyles";
import DataTable from "../../../components/dataTable";
import Datapicker from "@/components/hookForms/datePicker";
import DialogEdit from "./modal/dialogEdit";
import DialogList from "./modal/dialogList";
import DialogLoad from "./modal/dialogLoad";
import DialogImportPN from "./modal/dialogImportPN";
import IGenericResult from "@/interfaces/IGenericResult";
import { format } from "date-fns";
import messages from "@/utils/messages";
import serviceRebalanceUsinaHistoricoGeracao from "@/services/serviceRebalanceUsinaHistoricoGeracao";
import serviceUsina from "@/services/serviceUsina";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/global";
import { useForm } from "react-hook-form";
import serviceConcessionaria from "@/services/serviceConcessionaria";

const GeracaoUsinas: React.FC = () => {
    const { setTitlePage } = useAuth();

     useEffect(() => {
        setTitlePage("Rebalance - Geração das Usinas");
        setConcessionariaData();
    }, [setTitlePage]);

    const [usinaDataOptions, setUsinaDataOptions] = useState<IGenericResult[]>([]);
    const [data, setData] = useState<any[]>([]);
    const [pageSize, setPageSize] = useState(100);
    const [page, setPage] = useState(0);
    const [filters, setFilters] = useState(null);
    const [rowCount, setRowCount] = useState(0);
    const [isLoading, setLoading] = useState(false);
    const [openLoad, setOpenLoad] = useState(false);
    const [openModalList, setOpenModalList] = useState(false);
    const [listErros, setListErros] = useState<string[]>([]);
    const [concessionariaDataOptions, setConcessionariaDataOptions] = useState<IGenericResult[]>([]);
    const [openImportPN, setOpenImportPN] = useState(false);

    const [sortModel, setSortModel] = useState<GridSortModel>([
        {
            field: "",
            sort: "" as GridSortDirection,
        },
    ]);

    const [editState, setEditState] = useState({
        open: false,
        id: 0,
        geracaoPrevista: 0,
        nomeUsina: "",
        cicloFaturamento: "",
        producaoEspecifica: "",
        observacao: "",
        risco: "",
    });

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
        // eslint-disable-next-line react-hooks/rules-of-hooks
    } = useForm({
        defaultValues: {
            cicloFaturamento: null,
            usinaID: "",
            concessionariaID: "",
        },
    });

    const setConcessionariaData = async () => {
            serviceConcessionaria
                .getConcessionarias()
                .then((result) => {
                    setConcessionariaDataOptions(result?.data);
                })
                .catch(() => {
                    setConcessionariaDataOptions([]);
                });
        };

    const actionColumns = useMemo(
        () => [
            {
                headerName: "Operação",
                field: "operations",
                type: "actions",
                width: 100,
                hide: false,
                getActions: (params: { id: GridRowId; row: GridRowModel }) => {
                    return [
                        <GridActionsCellItem
                            icon={
                                <Tooltip title="Editar">
                                    <Edit />
                                </Tooltip>
                            }
                            label="Editar"
                            onClick={() => {
                                setEditState({
                                    open: true,
                                    id: Number(params.id),
                                    geracaoPrevista: params.row.geracaoPrevista,
                                    nomeUsina: params.row.nomeUsina,
                                    cicloFaturamento: params.row.cicloFaturamento,
                                    producaoEspecifica: params.row.producaoEspecifica,
                                    observacao: params.row.observacao,
                                    risco: params.row.risco,
                                });
                            }}
                            disabled={!params.row.ultimoCiclo}
                        />,
                    ];
                },
            },
        ],
        []
    );

    const columns = useMemo(
        () => [
            {
                headerName: "ID",
                field: "rebalanceUsinaHistoricoGeracaoID",
                type: "number",
                headerAlign: "left",
                identity: true,
                width: 70,
            },
            {
                headerName: "Usina",
                field: "nomeUsina",
                type: "string",
                width: 160,
            },
            {
                headerName: "Ciclo",
                field: "cicloFaturamento",
                type: "string",
                width: 160,
            },
            {
                headerName: "Geração",
                field: "geracaoPrevista",
                type: "number",
                width: 160,
            },
            {
                headerName: "Geração realizada",
                field: "geracaoRealizada",
                type: "number",
                width: 180,
            },
            {
                headerName: "Geração PN",
                field: "geracaoPN",
                type: "number",
                width: 160,
                sortable: false,
            },
            {
                headerName: "Produção específica",
                field: "producaoEspecifica",
                type: "number",
                width: 180,
            },
            {
                headerName: "Energia kWp",
                field: "energiaKWP",
                type: "number",
                width: 150,
            },
            {
                headerName: "Mês Reflexo",
                field: "cicloReflexo",
                type: "string",
                width: 160,
            },
            {
                headerName: "Risco",
                field: "risco",
                type: "string",
                width: 160,
            },
            {
                headerName: "Observação",
                field: "observacao",
                type: "string",
                width: 160,
            },
            {
                headerName: "Distribuidora",
                field: "nomeConcessionaria",
                type: "string",
                width: 160,
            },
            ...actionColumns,
        ],
        [actionColumns]
    );

    useEffect(() => {
        setUsinaData();
    }, []);

    const setUsinaData = async () => {
        serviceUsina
            .getUsinas()
            .then((result) => {
                setUsinaDataOptions(result?.data);
            })
            .catch(() => {
                setUsinaDataOptions([]);
            });
    };

    const getAllWithFilters = async (filters: any) => {
        setData([]);
        setLoading(true);

        serviceRebalanceUsinaHistoricoGeracao
            .getWithFilter({
                ...filters,
                take: pageSize,
                currentPage: page + 1,
                sortExpression: sortModel[0]?.field ?? "",
                sortDirection: sortModel[0]?.sort ?? "",
            })
            .then((result) => {
                if (result) {
                    setData(result?.data.content);
                    setRowCount(result?.data.numberOfElements);
                }
            })
            .catch((err) => {
                toast.error(messages.getWithFilterError, err);
                setData([]);
                setRowCount(0);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(
        () => {
            getAllWithFilters(filters);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [page, pageSize, sortModel]
    );

    const onSubmit = (values: any) => {
        const newValues = {
            ...values,
            cicloFaturamento: values.cicloFaturamento ? format(values.cicloFaturamento, "MM/yyyy") : "",
        };

        setFilters(newValues);
        getAllWithFilters(newValues);
    };

    const onClear = () => {
        reset();
        setFilters(null);
        getAllWithFilters(null);
    };

    const handleCancelLoad = () => {
        setOpenLoad(false);
    };

    const onSuccessAction = () => {
        getAllWithFilters(filters);
    };

    return (
        <Container>
            <Card>
                <CardContent>
                    <Box sx={{ mb: 2 }}>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <Grid container spacing={2}>
                                <Grid item md={12}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={1.8}>
                                            <AutoComplete
                                                name="usinaID"
                                                label="Usina"
                                                options={usinaDataOptions}
                                                control={control}
                                                error={errors}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={1.8}>
                                            <Datapicker
                                                name="cicloFaturamento"
                                                label="Ciclo"
                                                control={control}
                                                error={errors}
                                                format="MM/yyyy"
                                                view={["month", "year"]}
                                                noDateForward
                                            />
                                        </Grid>

                                        <Grid item xs={10} md={1.8}>
                                            <AutoComplete
                                                name="concessionariaID"
                                                label="Concessionária"
                                                options={concessionariaDataOptions}
                                                control={control}
                                                error={errors}
                                            />
                                        </Grid>

                                        <Grid item md={1.5}>
                                            <Button
                                                disableElevation
                                                variant="contained"
                                                color="warning"
                                                fullWidth
                                                style={{ margin: "16px 0px 8px 0px" }}
                                                startIcon={<Search />}
                                                type="submit"
                                                disabled={isLoading}
                                            >
                                                Pesquisar
                                            </Button>
                                        </Grid>

                                        <Grid item md={1.5}>
                                            <Button
                                                disableElevation
                                                variant="outlined"
                                                color="warning"
                                                fullWidth
                                                style={{ margin: "16px 0px 8px 0px" }}
                                                startIcon={<Clear />}
                                                onClick={() => onClear()}
                                            >
                                                Limpar
                                            </Button>
                                        </Grid>

                                        <Grid item md={1.5}>
                                            <Button
                                                disableElevation
                                                variant="contained"
                                                color="primary"
                                                fullWidth
                                                style={{ margin: "16px 0px 8px 0px" }}
                                                startIcon={<AddCircle />}
                                                onClick={() => setOpenLoad(true)}
                                            >
                                                Novo
                                            </Button>
                                        </Grid>

                                        <Grid item md={1.5}>
                                            <Button
                                                disableElevation
                                                variant="contained"
                                                color="primary"
                                                fullWidth
                                                style={{ margin: "16px 0px 8px 0px" }}
                                                startIcon={<Upload />}
                                                onClick={() => setOpenImportPN(true)}
                                            >
                                                Importação de PN
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </form>
                    </Box>

                    <Box sx={{ height: 450 }}>
                        <DataTable
                            columns={columns}
                            rows={data}
                            rowId={(row: GridRowModel) => row.rebalanceUsinaHistoricoGeracaoID}
                            rowCount={rowCount}
                            isLoading={isLoading}
                            onPageChange={(currentPage: number) => setPage(currentPage)}
                            onPageSizeChange={(currentPageSize: number) => setPageSize(currentPageSize)}
                            pageSize={pageSize}
                            onSortModelChange={(model: GridSortModel) => setSortModel(model)}
                        />
                    </Box>
                </CardContent>
            </Card>

            <DialogEdit
                open={editState.open}
                data={{
                    open: editState.open,
                    id: editState.id,
                    geracaoPrevista: editState.geracaoPrevista,
                    nomeUsina: editState.nomeUsina,
                    cicloFaturamento: editState.cicloFaturamento,
                    producaoEspecifica: editState.producaoEspecifica,
                    observacao: editState.observacao,
                    risco: editState.risco,
                }}
                onSuccess={() => {
                    onSuccessAction();
                }}
                onClose={() =>
                    setEditState({
                        open: false,
                        id: 0,
                        geracaoPrevista: 0,
                        nomeUsina: "",
                        cicloFaturamento: "",
                        producaoEspecifica: "",
                        observacao: "",
                        risco: "",
                    })
                }
            />

            <DialogLoad
                open={openLoad}
                onSuccess={() => {
                    onSuccessAction();
                }}
                onClose={() => {
                    handleCancelLoad();
                }}
                returnDate={(item) => {
                    setListErros(item);
                    setOpenModalList(true);
                }}
            />

            <DialogImportPN
                open={openImportPN}
                onSuccess={() => {
                    onSuccessAction();
                }}
                onClose={() => {
                    setOpenImportPN(false);
                }}
                returnDate={(item) => {
                    setListErros(item);
                    setOpenModalList(true);
                }}
            />

            {openModalList && <DialogList data={listErros} onClose={() => setOpenModalList(false)} />}
        </Container>
    );
};

export default GeracaoUsinas;
